import { useEffect, useState } from 'react';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import {
  loadReminderPrefs,
  saveReminderPrefs,
  syncReminderSubscription,
  sendTestNotification,
  notificationSupported,
  type ReminderPrefs,
} from '../lib/reminders';
import styles from '../App.module.css';

interface ReminderCardProps {
  accountName: string;
  uncheckedHint: string;
}

export function ReminderCard({ accountName, uncheckedHint }: ReminderCardProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const [prefs, setPrefs] = useState<ReminderPrefs>(() => loadReminderPrefs());
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const supported = notificationSupported();

  useEffect(() => {
    saveReminderPrefs(prefs);
  }, [prefs]);

  async function applyEnabled(enabled: boolean, hourJst: number) {
    setBusy(true);
    setStatus('');
    const next = { ...prefs, enabled, hourJst };
    setPrefs(next);
    const result = await syncReminderSubscription({
      enabled,
      hourJst,
      locale,
      accountName,
      uncheckedHint,
    });
    if (!result.ok) {
      setStatus(
        result.message === 'permission_denied'
          ? ui.reminder.permissionDenied
          : ui.reminder.syncFailed
      );
      if (enabled) setPrefs({ ...next, enabled: false });
    } else {
      setStatus(enabled ? ui.reminder.enabledOk : ui.reminder.disabledOk);
    }
    setBusy(false);
  }

  return (
    <section className={styles.installCard} aria-labelledby="reminder-title">
      <h2 id="reminder-title" className={styles.installTitle}>
        {ui.reminder.title}
      </h2>
      <p className={styles.installBody}>{ui.reminder.body}</p>
      {!supported ? (
        <p className={styles.installHint}>{ui.reminder.unsupported}</p>
      ) : (
        <>
          <label className={styles.onboardingField}>
            <span>{ui.reminder.hourLabel}</span>
            <select
              className={styles.onboardingInput}
              value={prefs.hourJst}
              onChange={(e) => {
                const hourJst = Number(e.target.value);
                setPrefs((p) => ({ ...p, hourJst }));
                if (prefs.enabled) void applyEnabled(true, hourJst);
              }}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {ui.reminder.hourOption.replace('{hour}', String(h).padStart(2, '0'))}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.shareActions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={busy}
              onClick={() => applyEnabled(!prefs.enabled, prefs.hourJst)}
            >
              {prefs.enabled ? ui.reminder.disable : ui.reminder.enable}
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              disabled={busy}
              onClick={async () => {
                const ok = await sendTestNotification(ui.reminder.testTitle, ui.reminder.testBody);
                setStatus(ok ? ui.reminder.testOk : ui.reminder.permissionDenied);
              }}
            >
              {ui.reminder.test}
            </button>
          </div>
          <p className={styles.installNote}>{ui.reminder.iosNote}</p>
        </>
      )}
      {status ? (
        <p className={styles.shareStatus} role="status">
          {status}
        </p>
      ) : null}
    </section>
  );
}
